import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LoadingContainer } from "../components/loading-container.js";
import { firebaseFetchAllPostsForSearch } from "../redux/firebase.js";

const stripHtml = (html) => {
  if (!html) return "";
  return String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
};

/** Post ID for URL: Firebase key may be "en-buenos-aires-c10-11", route expects "buenos-aires-c10-11". */
const postIdForUrl = (id, lang) =>
  id && lang && id.startsWith(lang + "-") ? id.slice((lang + "-").length) : id;

/** Use only the title for the current language when title contains " / " (e.g. "RU / EN"). */
const titleForLanguage = (title, lang) => {
  if (!title || !title.includes(" / ")) return title || "";
  const parts = title.split(" / ").map((s) => s.trim());
  return lang === "en" ? (parts[1] ?? parts[0]) : (parts[0] ?? parts[1]);
};

/** Turn post content (string or trip array) into one searchable plain-text string. */
const contentToSearchText = (content) => {
  if (!content) return "";
  if (typeof content === "string") return stripHtml(content);
  if (!Array.isArray(content)) return "";
  return content
    .filter((e) => e && (e.type === "text" || e.type === "section"))
    .map((e) => stripHtml(e.src || ""))
    .join(" ");
};

/** Build a short snippet with the first match highlighted in <b>. */
const buildSnippet = (text, query, maxLen = 300) => {
  const plain = stripHtml(text);
  if (!plain) return "";
  const q = query.trim();
  if (!q) return plain.length <= maxLen ? plain : plain.slice(0, maxLen) + "...";
  const lower = plain.toLowerCase();
  const lowerQ = q.toLowerCase();
  const idx = lower.indexOf(lowerQ);
  if (idx === -1) return plain.length <= maxLen ? plain : plain.slice(0, maxLen) + "...";
  const start = Math.max(0, idx - 80);
  const end = Math.min(plain.length, idx + q.length + 80);
  let snippet = (start > 0 ? "..." : "") + plain.slice(start, end) + (end < plain.length ? "..." : "");
  // Highlight all case-insensitive occurrences of query in snippet
  const re = new RegExp("(" + q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+") + ")", "gi");
  snippet = snippet.replace(re, "<b>$1</b>");
  return snippet;
};

const SearchPage = () => {
  const { lang, query } = useParams();
  const language = lang || "en";

  const { data: posts = [], isLoading, isFetching } = useQuery({
    queryKey: ["searchIndex", language],
    queryFn: () => firebaseFetchAllPostsForSearch(language),
    staleTime: 5 * 60 * 1000,
  });

  const trimmedQuery = (query || "").trim();
  const results = React.useMemo(() => {
    if (!trimmedQuery) return [];
    const lowerQ = trimmedQuery.toLowerCase();
    const filtered = posts.filter((p) => {
      const inTitle = (p.title || "").toLowerCase().includes(lowerQ);
      const inPreview = (stripHtml(p.preview) || "").toLowerCase().includes(lowerQ);
      const fullText = contentToSearchText(p.content);
      const inContent = fullText.toLowerCase().includes(lowerQ);
      return inTitle || inPreview || inContent;
    });
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [posts, trimmedQuery]);

  if (isLoading || isFetching) return <LoadingContainer />;

  const noQuery = !trimmedQuery;
  const noResults = !noQuery && results.length === 0;

  return (
    <div className="search-results">
      <h1 className="text-2xl font-bold mb-4 text-slate-800">
        {language === "ru" ? "Поиск" : "Search"}
        {trimmedQuery && `: "${trimmedQuery}"`}
      </h1>

      {noQuery && (
        <p className="text-slate-500">
          {language === "ru" ? "Введите запрос в поле поиска справа." : "Enter a query in the search box on the right."}
        </p>
      )}

      {noResults && (
        <p className="text-gray-600">
          {language === "ru" ? "Ничего не найдено." : "No results found."}
        </p>
      )}

      {!noQuery && !noResults && (
        <ul className="space-y-4">
          {results.map((entry) => (
            <li key={entry.id} className="card p-4 flex flex-col">
              <p>
                <Link className="tripLink text-lg font-semibold" to={`/${language}/post/${postIdForUrl(entry.id, language)}`}>
                  {titleForLanguage(entry.title, language)}
                </Link>
              </p>
              {entry.date && <div className="text-xs text-slate-500 mt-0.5">{entry.date}</div>}
              <p
                className="searchResultText mt-1"
                dangerouslySetInnerHTML={{
                  __html: (() => {
                    const fullText = contentToSearchText(entry.content);
                    const inContent = fullText.toLowerCase().includes(trimmedQuery.toLowerCase());
                    return buildSnippet(inContent ? fullText : entry.preview || "", trimmedQuery);
                  })(),
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SearchPage;
